import React, { FC, useState, useContext } from 'react'
import { CheckCircleIcon, PlusCircleIcon, ExternalLinkIcon } from '@heroicons/react/outline'
import { useSelector, useDispatch } from 'react-redux'
import { useApolloClient } from '@apollo/client'
import { useHistory } from 'react-router-dom'
import { StyledChallengeOptIn } from '.'
import { AppState } from '../../redux/root-reducer'
import { ChallengeState } from '../../redux/challenge/types'
import { JoinChallenge } from '../../graphql/Challenge/__generated__/JoinChallenge'
import { JoinChallengeMutation } from '../../graphql/Challenge/joinChallenge'
import { ChallengeChangeUserDataAction } from '../../redux/challenge/actions'
import { useNotifications } from '../Notifications'
import { SplitContext } from '../utils/SplitContext'
import ROUTES from '../utils/routes'

const ChallengeOptIn: FC = () => {
  const apollo = useApolloClient()
  const dispatch = useDispatch()
  const history = useHistory()
  const { addNotification } = useNotifications()
  const [processing, setProcessing] = useState(false)
  const { currentChallenge, userChallenge } = useSelector<AppState, ChallengeState>(
    (state) => state.challenge
  )

  const {
    isReady,
    isEnabled,
    treatments: { showDashboardChallenge }
  } = useContext(SplitContext)

  const joinByDate = new Date(currentChallenge?.joinBy || 0).valueOf()
  const tooLateToJoin = joinByDate < new Date().valueOf() && !userChallenge

  if (!currentChallenge || tooLateToJoin) {
    return null
  }

  const joined = !!userChallenge

  const handleButtonText = () => {
    if (userChallenge?.completed) {
      return (
        <>
          <CheckCircleIcon /> Completed
        </>
      )
    }

    if (joined) {
      return 'Continue Challenge'
    }

    return (
      <>
        <PlusCircleIcon /> Join Challenge
      </>
    )
  }

  const handleRedirect = () => {
    if (joined) {
      history.push(ROUTES.dashboard.drafts)
    }
  }

  const joinChallenge = async () => {
    try {
      if (joined) {
        return
      }

      setProcessing(true)
      const { data } = await apollo.mutate<JoinChallenge>({
        mutation: JoinChallengeMutation,
        variables: { challenge: currentChallenge._id }
      })

      dispatch(ChallengeChangeUserDataAction(data?.joinChallenge!))

      if (currentChallenge.redirect) {
        history.push(currentChallenge.redirect)
        return
      }
    }
    catch (err) {
      addNotification({ message: err.message, type: 'error', interval: 5000 })
    }

    setProcessing(false)
  }

  const showOptIn = isReady && isEnabled(showDashboardChallenge)
  if (!showOptIn) {
    return null
  }

  return (
    <StyledChallengeOptIn joined={joined} onClick={handleRedirect}>
      <div>
        <span className="challenge-title">{currentChallenge.name}</span>
        {!userChallenge && (
          <a href={currentChallenge.learnMore} rel="noopener noreferrer" target="_blank">
            Learn More <ExternalLinkIcon />
          </a>
        )}
      </div>
      <button onClick={joinChallenge} disabled={processing}>
        {handleButtonText()}
      </button>
    </StyledChallengeOptIn>
  )
}

export default ChallengeOptIn
